<template>
  <div class="salon-app salon-date">
    <!-- Top app bar + booking progress -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.date.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <span class="salon-progress-label">{{ $t("salon.date.stepOf", { step, total: totalSteps }) }}</span>
      </div>
    </header>

    <main class="salon-main">
      <SalonFlowToggle v-if="flowToggle" order="date" @switch="$emit('switch-flow', $event)" />

      <h2 class="salon-date-title">{{ $t("salon.date.heading") }}</h2>

      <!-- SH026 — staff-first: these are the days this stylist can take, so say whose
           calendar is on screen. -->
      <p v-if="staffName" class="salon-date-lead">{{ $t("salon.date.showingStaff", { name: staffName }) }}</p>

      <!-- Month navigation -->
      <div class="salon-month">
        <div class="salon-month-left">
          <h3 class="salon-month-name">{{ monthLabel }}</h3>
          <div class="salon-month-nav">
            <button
              class="salon-month-btn"
              type="button"
              :aria-label="$t('salon.date.previousMonth')"
              :disabled="!canGoBack"
              @click="shiftMonth(-1)"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <button class="salon-month-btn" type="button" :aria-label="$t('salon.date.nextMonth')" @click="shiftMonth(1)">
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>
        <button class="salon-full-calendar" type="button" @click="isCalendarOpen = true">
          {{ $t("salon.date.fullCalendar") }}
        </button>
      </div>

      <!-- Day chips. Past days are dropped rather than dimmed: there is nothing to pick
           there, and on the 28th of a month they would push every bookable day off screen. -->
      <div ref="strip" class="salon-strip">
        <button
          v-for="day in stripDays"
          :key="day.date"
          class="salon-chip"
          :class="{
            'salon-chip--selected': day.date === selectedDate,
            'salon-chip--off': !day.is_selectable,
          }"
          type="button"
          :data-date="day.date"
          :disabled="!day.is_selectable"
          :aria-label="day.hours_text"
          :aria-pressed="String(day.date === selectedDate)"
          @click="selectedDate = day.date"
        >
          <span class="salon-chip-day">{{ dayShortOf(day) }}</span>
          <span class="salon-chip-number">{{ day.day_number }}</span>
          <span v-if="day.is_staff_off" class="salon-chip-tag">{{ $t("salon.date.off") }}</span>
          <!-- SH037 — the salon is open and the stylist is in; this service is simply not
               sold that day, which is neither "Off" nor "Full". -->
          <span v-else-if="day.is_service_off" class="salon-chip-tag">{{ $t("salon.date.notOffered") }}</span>
          <span v-else-if="day.is_open && day.is_full" class="salon-chip-tag">{{ $t("salon.date.full") }}</span>
          <span v-else-if="!day.is_open" class="salon-chip-tag">{{ $t("salon.date.closed") }}</span>
          <span v-else-if="day.date === selectedDate" class="salon-chip-dot" aria-hidden="true"></span>
        </button>

        <p v-if="!isLoading && !stripDays.length" class="salon-strip-empty">
          {{ loadFailed
            ? $t("salon.date.loadError")
            : $t("salon.date.emptyMonth") }}
        </p>
      </div>

      <button v-if="loadFailed" class="salon-retry" type="button" @click="getDates">{{ $t("salon.date.retry") }}</button>

      <!-- Opening hours for whichever day is selected -->
      <div v-if="selectedDay" class="salon-info">
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6" />
          <path d="M12 11v5M12 8h.01" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" />
        </svg>
        <p>{{ selectedDay.hours_text }}</p>
      </div>

      <!-- What they are booking, carried over from SH004/SH005 -->
      <div v-if="services.length" class="salon-summary">
        <h4 class="salon-summary-title">{{ servicesText }}</h4>
        <p class="salon-summary-meta">{{ summaryMeta }}</p>
      </div>
    </main>

    <nav class="salon-cta">
      <div class="salon-cta-info">
        <span class="salon-cta-label">{{ $t("salon.date.selectedDate") }}</span>
        <span class="salon-cta-value">{{ selectedDateText || "—" }}</span>
      </div>
      <button class="salon-cta-btn" type="button" :disabled="!selectedDate" @click="goToNextStep">
        <span>{{ $t("salon.date.continue") }}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M5 12h14m-6-6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </nav>

    <SalonMonthCalendarSheet
      v-if="isCalendarOpen"
      :days="days"
      :month-text="monthLabel"
      :selected-date="selectedDate"
      :today="today"
      @close="isCalendarOpen = false"
      @select="onCalendarPick"
    />

    <material-loading :active="isLoading" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonFlowToggle from "@/salon/components/FlowToggle.vue";
import SalonMonthCalendarSheet from "@/salon/components/MonthCalendarSheet.vue";
import SalonBookingService from "@/services/salonBooking.service";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH006 is the sixth of the nine salon booking steps (Salon_UI/select_date) — unless
// SH026's channel setting puts the stylist first, which is what the `step` prop is for.
const STEP = 6;
const TOTAL_STEPS = 9;

// SH046 — "YYYY-MM-DD" as a UTC midnight, formatted in UTC too, so no timezone can move
// it onto the previous day. Null when the string is not a date.
function utcDate(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day));
}

function zhFormat(date, options) {
  return new Intl.DateTimeFormat("zh-CN", { timeZone: "UTC", ...options }).format(date);
}

export default {
  name: "SalonSelectDate",
  components: {
    SalonFlowToggle,
    SalonProfileMenu,
    MaterialLoading,
    SalonMonthCalendarSheet,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    branchCode: {
      type: String,
      default: "",
    },
    branchName: {
      type: String,
      default: "",
    },
    // The services chosen at SH004 and configured at SH005, each already merged with
    // its add-ons, note and total price.
    services: {
      type: Array,
      default: () => [],
    },
    // Set when the customer is coming back from SH007. This view is destroyed on the way
    // out, so without it Back would land them on the 1st of the month rather than the
    // day they had already chosen.
    initialDate: {
      type: String,
      default: "",
    },
    // SH032 — minty_branches.salon_show_service_price, carried down from the outlet.
    showPrice: {
      type: Boolean,
      default: false,
    },
    // SH026 — the stylist already chosen on a staff-first channel. The month is then read
    // off their calendar rather than the whole channel's, and their days off are marked.
    staffId: {
      type: [Number, String],
      default: "",
    },
    staffName: {
      type: String,
      default: "",
    },
    step: {
      type: Number,
      default: STEP,
    },
    // Whether the customer may switch to picking a stylist first from here.
    flowToggle: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["back", "selected", "profile", "switch-flow"],
  data() {
    return {
      totalSteps: TOTAL_STEPS,
      isLoading: false,
      loadFailed: false,
      isCalendarOpen: false,
      // "" until the first response arrives — the API decides which month is current,
      // so the browser's clock never gets a say in what counts as a past date.
      month: this.initialDate ? this.initialDate.slice(0, 7) : "",
      monthText: "",
      today: "",
      days: [],
      selectedDate: this.initialDate,
    };
  },
  computed: {
    progressWidth() {
      return ((this.step / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },

    // SH046 — the API's "September 2026" stays as it is in English; Chinese is rebuilt
    // from the month key ("2026年9月") so it also follows a later language switch.
    monthLabel() {
      if (this.$i18n.locale !== "zh" || !this.month) {
        return this.monthText;
      }

      const moment = utcDate(this.month + "-01");

      return moment ? zhFormat(moment, { year: "numeric", month: "long" }) : this.monthText;
    },

    // Only a real stylist narrows the month; 0 is "No Preference", which is the whole
    // channel's calendar — the same question a date-first booking asks.
    staffFilter() {
      return Number(this.staffId) > 0 ? Number(this.staffId) : undefined;
    },

    // SH037 — the item ids behind the chosen services, so the month can answer for the
    // hours those services are actually sold in. Left out entirely when nothing has been
    // chosen, which is what tells the API to restrict nothing.
    serviceIds() {
      const ids = this.services
        .map((service) => Number(service.item_id || service.id || 0))
        .filter((id) => id > 0);

      return ids.length ? ids : undefined;
    },

    stripDays() {
      return this.days.filter((day) => !day.is_past);
    },

    selectedDay() {
      return this.days.find((day) => day.date === this.selectedDate) || null;
    },

    canGoBack() {
      // Nobody books backwards, so the arrow stops at the month today sits in.
      return this.month > this.today.slice(0, 7);
    },

    // The booking's whole duration: each service plus whatever add-ons were ticked for
    // it in the SH005 sheet, since those extend the appointment too.
    totalDuration() {
      return this.services.reduce((total, service) => {
        const addOns = (service.add_ons || []).reduce(
          (sum, addOn) => sum + Number(addOn.duration_min || 0),
          0
        );

        return total + Number(service.duration_min || 0) + addOns;
      }, 0);
    },

    totalPrice() {
      return this.services.reduce(
        // total_price is only there once the service has been through the detail sheet.
        (total, service) => total + Number(service.total_price ?? service.price ?? 0),
        0
      );
    },

    servicesText() {
      return this.services.map((service) => service.name).join(" + ");
    },

    summaryMeta() {
      const parts = [];

      if (this.totalDuration) {
        parts.push(this.$t("salon.date.minutes", { n: this.totalDuration }));
      }

      // SH032 — with prices off the summary is the duration alone, so the customer is
      // not quoted a figure here that the service picker just declined to show them.
      if (this.showPrice) {
        parts.push("$" + this.totalPrice.toFixed(2));
      }

      return parts.join(" · ");
    },

    // "Tue, 15 Sep 2026" — built from the API's own fields rather than the Date object,
    // which would re-parse the ISO string as UTC and can name the previous day.
    selectedDateText() {
      const day = this.selectedDay;

      if (!day) {
        return "";
      }

      // SH046 — "2026年9月15日 周二" in Chinese; English keeps the line below untouched.
      if (this.$i18n.locale === "zh") {
        const moment = utcDate(day.date);

        return moment
          ? zhFormat(moment, { year: "numeric", month: "long", day: "numeric" }) +
              " " + zhFormat(moment, { weekday: "short" })
          : day.date;
      }

      const [year, monthNumber] = day.date.split("-");
      const shortMonth = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ][Number(monthNumber) - 1];

      return day.day_short + ", " + day.day_number + " " + shortMonth + " " + year;
    },
  },
  created() {
    this.getDates();
  },
  methods: {
    async getDates() {
      if (!this.branchCode) {
        return;
      }

      this.isLoading = true;
      this.loadFailed = false;

      try {
        const response = await SalonBookingService.getAvailableDates({
          branch_code: this.branchCode,
          month: this.month || undefined,
          duration_min: this.totalDuration || undefined,
          staff_id: this.staffFilter,
          service_ids: this.serviceIds,
        });

        if (response.data.result.isSuccess) {
          const data = response.data.data;

          this.month = data.month;
          this.monthText = data.month_text;
          this.today = data.today;
          this.days = data.days ?? [];
          this.applyDefaultSelection();
        } else {
          this.days = [];
          this.loadFailed = true;
        }
      } catch (error) {
        // Swallowing this would render an outlet with a genuinely empty calendar
        // identically to one whose endpoint is broken.
        this.days = [];
        this.loadFailed = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Keep the customer's pick if the month they came back to still offers it,
    // otherwise land on the first day that can actually be booked.
    applyDefaultSelection() {
      const current = this.days.find((day) => day.date === this.selectedDate);

      if (current && current.is_selectable) {
        this.scrollToSelected();
        return;
      }

      const first = this.days.find((day) => day.is_selectable);
      this.selectedDate = first ? first.date : "";
      this.scrollToSelected();
    },

    shiftMonth(offset) {
      if (offset < 0 && !this.canGoBack) {
        return;
      }

      const [year, month] = this.month.split("-").map(Number);
      // Month 0 and month 13 are how Date normalises a year boundary; doing the
      // arithmetic by hand here is what usually produces "2026-00".
      const shifted = new Date(Date.UTC(year, month - 1 + offset, 1));

      this.month =
        shifted.getUTCFullYear() +
        "-" +
        String(shifted.getUTCMonth() + 1).padStart(2, "0");

      this.getDates();
    },

    // SH046 — the chip's weekday: the API's "Tue" in English, "周二" in Chinese.
    dayShortOf(day) {
      if (this.$i18n.locale !== "zh") {
        return day.day_short;
      }

      const moment = utcDate(day.date);

      return moment ? zhFormat(moment, { weekday: "short" }) : day.day_short;
    },

    onCalendarPick(day) {
      this.selectedDate = day.date;
      this.isCalendarOpen = false;
      this.scrollToSelected();
    },

    // The strip can hold a month's worth of chips, so the chosen day is rarely the one
    // on screen after a month change or a pick from the calendar sheet.
    scrollToSelected() {
      this.$nextTick(() => {
        const strip = this.$refs.strip;

        if (!strip || !this.selectedDate) {
          return;
        }

        // querySelector, not $refs: this bundle pins Vue 3.2.0, where a `ref` inside
        // v-for does not collect into an array and comes back undefined.
        const chip = strip.querySelector('[data-date="' + this.selectedDate + '"]');

        if (!chip) {
          return;
        }

        // Measured against the strip itself, not offsetLeft: the strip is not positioned,
        // so offsetLeft is counted from an ancestor further up and carries that element's
        // padding with it — which scrolled the first chip half off the screen edge.
        const left =
          strip.scrollLeft +
          chip.getBoundingClientRect().left -
          strip.getBoundingClientRect().left -
          20;

        strip.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
      });
    },

    goToNextStep() {
      if (!this.selectedDay) {
        return;
      }

      // SH007 (pick a time) reads the outlet's hours for this day off the same row, so
      // the whole day travels rather than just its date string.
      this.$emit("selected", {
        date: this.selectedDay.date,
        date_text: this.selectedDateText,
        day: this.selectedDay,
        duration_min: this.totalDuration,
      });
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-date {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--salon-on-surface-variant);
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-progress {
  margin-top: 8px;
  text-align: center;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-border);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.salon-progress-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  flex: 1;
  padding: 24px var(--salon-container-padding) 140px;
}

.salon-date-title {
  margin: 0 0 var(--salon-stack-lg);
  font-family: var(--salon-font-headline);
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.01em;
  color: var(--salon-on-background);
}

/* SH026 — whose calendar the strip is showing, when the stylist came first. */
.salon-date-lead {
  margin: calc(var(--salon-stack-md) * -1) 0 var(--salon-stack-md);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Month navigation ──────────────────────────────────────────── */
.salon-month {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--salon-stack-md);
}

.salon-month-left {
  display: flex;
  gap: 16px;
  align-items: center;
}

.salon-month-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-background);
}

.salon-month-nav {
  display: flex;
  gap: 4px;
}

.salon-month-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.salon-month-btn:hover:not(:disabled) {
  background: var(--salon-surface-variant);
}

.salon-month-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.salon-month-btn:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.salon-full-calendar {
  padding: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-secondary);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-full-calendar:hover {
  opacity: 0.8;
}

/* ── Day chips ─────────────────────────────────────────────────── */
.salon-strip {
  display: flex;
  gap: 12px;
  padding: 0 var(--salon-container-padding) 8px;
  /* Bleeds to the screen edge so the strip reads as scrollable, then re-pads inside. */
  margin: 0 calc(-1 * var(--salon-container-padding)) var(--salon-stack-lg);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  /* Snapping aligns a chip with the scrollport edge, not the content box, so without
     this the snap undoes the scroll and jams the chosen chip against the screen edge. */
  scroll-padding-left: var(--salon-container-padding);
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.salon-strip::-webkit-scrollbar {
  display: none;
}

.salon-chip {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 4px;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 76px;
  padding: 0;
  font-family: var(--salon-font-body);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: 12px;
  cursor: pointer;
  scroll-snap-align: start;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.salon-chip:hover:not(:disabled) {
  border-color: var(--salon-primary);
}

.salon-chip--selected {
  background: var(--salon-primary);
  border-color: var(--salon-primary);
  box-shadow: 0 4px 10px rgba(28, 28, 30, 0.18);
}

.salon-chip--off {
  background: var(--salon-surface-container);
  border-color: transparent;
  cursor: not-allowed;
  opacity: 0.5;
}

.salon-chip-day {
  font-size: 10px;
  line-height: 12px;
  color: var(--salon-on-surface-variant);
  text-transform: uppercase;
}

.salon-chip--selected .salon-chip-day {
  color: rgba(255, 255, 255, 0.8);
}

.salon-chip-number {
  font-size: 22px;
  font-weight: 600;
  line-height: 26px;
  color: var(--salon-on-background);
}

.salon-chip--selected .salon-chip-number {
  color: var(--salon-on-primary);
}

.salon-chip--off .salon-chip-number {
  color: var(--salon-on-surface-variant);
  text-decoration: line-through;
}

.salon-chip-tag {
  font-size: 10px;
  line-height: 12px;
  color: var(--salon-error);
}

.salon-chip-dot {
  position: absolute;
  bottom: 8px;
  width: 4px;
  height: 4px;
  background: var(--salon-secondary-container);
  border-radius: 999px;
}

.salon-strip-empty {
  padding: 24px 0;
  margin: 0;
  font-size: 14px;
  color: var(--salon-on-surface-variant);
}

.salon-retry {
  display: block;
  padding: 8px 20px;
  margin: 0 auto var(--salon-stack-lg);
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  color: var(--salon-primary);
  background: transparent;
  border: 1px solid var(--salon-surface-container-highest);
  border-radius: 999px;
  cursor: pointer;
}

/* ── Opening hours ─────────────────────────────────────────────── */
.salon-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  margin-bottom: var(--salon-stack-lg);
  background: var(--salon-surface-container-low);
  border-radius: 12px;
  color: var(--salon-on-surface-variant);
}

.salon-info svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.salon-info p {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
}

/* ── Booking summary ───────────────────────────────────────────── */
.salon-summary {
  padding: 16px;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: var(--salon-radius-card);
  box-shadow: var(--salon-shadow-soft);
}

.salon-summary-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-background);
}

.salon-summary-meta {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

/* ── Bottom CTA ────────────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  z-index: 50;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(16px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface);
  border-top: 1px solid var(--salon-border);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-info {
  display: flex;
  flex-direction: column;
}

.salon-cta-label {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

.salon-cta-value {
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-background);
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(28, 28, 30, 0.18);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
